import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertBanner } from '@/components/campus';
import { Loader2, CheckCircle } from 'lucide-react';

export const Signup = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    try {
      const displayName = `${firstName} ${lastName}`.trim();
      const result = await signUp(email, password, displayName);
      
      // Check if email confirmation is required
      if (result.user && !result.session) {
        setSuccess(true);
      } else if (result.session) {
        // Auto-confirmed, redirect to onboarding
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="signup-success" className="text-center">
        <div className="w-16 h-16 rounded-full bg-cool-mint/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-cool-mint" />
        </div>
        <h1 className="text-2xl font-semibold text-cloud-white tracking-[-0.02em] mb-2">
          Check your email
        </h1>
        <p className="text-soft-silver text-sm mb-6">
          We've sent a confirmation link to <span className="text-cloud-white">{email}</span>
        </p>
        <p className="text-muted-text text-xs">
          Click the link in your email to activate your account and continue.
        </p>
        <Link 
          to="/login" 
          className="inline-block mt-6 text-electric-ice-blue hover:underline text-sm"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="signup-page">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-cloud-white tracking-[-0.02em]">
          Create account
        </h1>
        <p className="text-soft-silver text-sm mt-2">
          Join the campus social network
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <AlertBanner 
            variant="error" 
            message={error} 
            dismissible 
            onDismiss={() => setError('')}
          />
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-soft-silver">First name</Label>
            <Input
              id="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
              data-testid="signup-firstname"
              required
              disabled={isLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-soft-silver">Last name</Label>
            <Input
              id="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
              data-testid="signup-lastname"
              required
              disabled={isLoading}
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email" className="text-soft-silver">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@university.edu"
            className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
            data-testid="signup-email"
            required
            disabled={isLoading}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password" className="text-soft-silver">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className="bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text focus:border-electric-ice-blue"
            data-testid="signup-password"
            required
            disabled={isLoading}
            minLength={6}
          />
          <p className="text-xs text-muted-text">At least 6 characters</p>
        </div>
        
        <Button 
          type="submit" 
          className="w-full bg-electric-ice-blue text-white hover:bg-electric-ice-blue/90 font-medium"
          data-testid="signup-submit"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating account...
            </>
          ) : (
            'Create account'
          )}
        </Button>
      </form>
      
      <p className="text-center text-sm text-soft-silver mt-6">
        Already have an account?{' '}
        <Link 
          to="/login" 
          className="text-electric-ice-blue hover:underline"
          data-testid="login-link"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
};

export default Signup;
